import { API_BASE_URL } from "../../../config";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select'; // Import React-Select
import useFetchUserSeller from '../../Utils/useFetchUserSeller';

const SalePriceListCreate = () => {
    const [itemTypes, setItemTypes] = useState([]);
    const [formData, setFormData] = useState({
        userId: '',
        prices: {},
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [token, setToken] = useState('');
    const navigate = useNavigate();

    const handleLogout = useCallback(() => {
        localStorage.removeItem('user');
        navigate('/login');
    }, [navigate]);

    const userSellerList = useFetchUserSeller(handleLogout);

    const checkToken = useCallback(() => {
        const user = localStorage.getItem('user');
        if (!user) {
            handleLogout();
            return null;
        }
        const retrievedToken = JSON.parse(user)?.token;
        setToken(retrievedToken);
        return retrievedToken;
    }, [handleLogout]);

    const fetchData = useCallback(async () => {
        const token = checkToken();
        if (!token) return;

        try {
            const itemTypesResponse = await fetch(`${API_BASE_URL}/seller/api/product-items`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (!itemTypesResponse.ok) {
                if (itemTypesResponse.status === 403) {
                    handleLogout();
                    return;
                }
                const errorText = await itemTypesResponse.text();
                setError(`Failed to fetch product items: ${errorText}`);
                throw new Error(`Failed to fetch product items: ${errorText}`);
            }

            const itemTypesData = await itemTypesResponse.json();
            const rawItems = Array.isArray(itemTypesData) ? itemTypesData : (Array.isArray(itemTypesData?.data) ? itemTypesData.data : []);
            const normalizedItems = rawItems
                .map(it => ({
                    id: it.id,
                    name: it.productName || it.name || '',
                    productCode: it.productCode,
                    description: it.description,
                    active: !!it.active
                }))
                .filter(it => typeof it.id === 'number' && it.name);
            setItemTypes(normalizedItems);

            const initialPrices = {};
            normalizedItems.forEach(item => {
                initialPrices[item.id] = '';
            });
            setFormData(prev => ({ ...prev, prices: initialPrices }));
        } catch (error) {
            setError('Error fetching product items');
            console.error('Fetch error:', error);
        } finally {
            setLoading(false);
        }
    }, [checkToken, handleLogout]);

    const handleUserChange = async (selectedOption) => {
        setFormData(prev => ({ ...prev, userId: selectedOption.value }));

        // Fetch default prices per item by iceCode and selected seller
        try {
            const token = checkToken();
            if (!token) return;

            // Build requests per item using its productCode as iceCode
            const requests = itemTypes.map(async (item) => {
                const iceCode = item.productCode || item.code || item.product_code;
                if (!iceCode) {
                    return { itemId: item.id, price: 0 };
                }
                const url = `${API_BASE_URL}/seller/api/default-prices/ice/${encodeURIComponent(iceCode)}/seller/${encodeURIComponent(selectedOption.value)}`;
                try {
                    const res = await fetch(url, {
                        method: 'GET',
                        headers: {
                            'Accept': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        },
                    });
                    if (res.status === 401 || res.status === 403) {
                        handleLogout();
                        return { itemId: item.id, price: 0 };
                    }
                    if (res.status === 404) {
                        // No default price for this (iceCode, seller)
                        return { itemId: item.id, price: 0 };
                    }
                    if (!res.ok) {
                        const txt = await res.text();
                        console.warn('Failed default price fetch for', iceCode, txt);
                        return { itemId: item.id, price: 0 };
                    }
                    const dp = await res.json(); // Expected DefaultPrice { id, iceCode, sellerId, price, currency }
                    const p = typeof dp?.price === 'number' ? dp.price : parseFloat(dp?.price);
                    return { itemId: item.id, price: isNaN(p) ? 0 : p };
                } catch (err) {
                    console.error('Error fetching default price for', iceCode, err);
                    return { itemId: item.id, price: 0 };
                }
            });

            const results = await Promise.all(requests);
            const updatedPrices = { ...formData.prices };
            results.forEach(({ itemId, price }) => {
                updatedPrices[itemId] = price || 0;
            });

            setFormData(prev => ({
                ...prev,
                prices: updatedPrices,
            }));
        } catch (error) {
            setError('Error fetching default prices for the selected user.');
            console.error('Fetch error:', error);
        }
    };


    const handlePriceChange = (e, itemId) => {
        const { value } = e.target;
        setFormData(prev => ({
            ...prev,
            prices: {
                ...prev.prices,
                [itemId]: value,
            },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const token = checkToken();
        if (!token) return;

        const { userId, prices } = formData;

        // Filter out items with price 0 and only send valid items
        const validPrices = Object.entries(prices).filter(([itemTypeId, price]) => price > 0);



        try {
            // Build payloads for DefaultPrice create: { iceCode, sellerId, price }
            const validWithIce = validPrices
                .map(([itemTypeId, price]) => {
                    const item = itemTypes.find(it => String(it.id) === String(itemTypeId));
                    const iceCode = item?.productCode || item?.code || item?.product_code;
                    if (!iceCode) {
                        console.warn('Skipping item without iceCode/productCode:', item);
                        return null;
                    }
                    return { iceCode, itemTypeId, price };
                })
                .filter(Boolean);

            const promises = validWithIce.map(({ iceCode, itemTypeId, price }) =>
                fetch(`${API_BASE_URL}/seller/api/default-prices`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        iceCode: String(iceCode),
                        sellerId: userId, // UID string from selector
                        price: parseFloat(price),
                    }),
                })
            );

            const responses = await Promise.all(promises);
            const failed = responses.filter(response => !response.ok);

            if (failed.length > 0) {
                setError(`Failed to create sale price lists for ${failed.length} item(s).`);
                return;
            }

            navigate('/salepriceAndItemManage');
        } catch (error) {
            setError('Error creating sale price list');
            console.error('Submit error:', error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    return (
        <div className="itemTypeManagementContainer">
            <h1>Create Sale Price List</h1>
            <button onClick={() => navigate('/salepriceAndItemManage')} className="backButton">
                กลับ
            </button>
            {loading ? (
                <p>Loading...</p>
            ) : (
                <form onSubmit={handleSubmit}>
                    {error && <p className="error">{error}</p>}
                    <div className="form-group">
                        <label htmlFor="userId">ผู้ใช้:</label>
                        <Select
                            id="userId"
                            name="userId"
                            options={(userSellerList || []).map(user => ({
                                value: user.uid,
                                label: `${user.nameuser} (${user.username})`,
                                username: user.username,
                                nameuser: user.nameuser,
                                uid: user.uid,
                            }))}
                            onChange={handleUserChange}
                            placeholder="เลือกผู้ใช้..."
                            formatOptionLabel={(option) => (
                                <div>
                                    <span style={{ fontWeight: 'bold' }}>{option.nameuser || option.label}</span>{' '}
                                    <span style={{ fontSize: 'small', color: 'gray' }}>({option.username})</span>{' '}
                                    <span style={{ fontSize: 'x-small', color: '#888' }}>{option.uid}</span>
                                </div>
                            )}
                            filterOption={(option, inputValue) => {
                                const q = (inputValue || '').toLowerCase();
                                const { label, data } = option;
                                return (
                                    (label || '').toLowerCase().includes(q) ||
                                    (data?.username || '').toLowerCase().includes(q) ||
                                    (data?.nameuser || '').toLowerCase().includes(q) ||
                                    (data?.uid || '').toLowerCase().includes(q)
                                );
                            }}
                            className="btc-selectBox-container"
                            classNamePrefix="btc-selectBox"
                        />
                    </div>
                    <div className="form-group">
                        <h3>ตั้งราคาสินค้าให้กับผู้ใช้</h3>
                        {itemTypes.map(itemType => (
                            <div key={itemType.id} className="form-item">
                                <label htmlFor={`price-${itemType.id}`}>
                                    {itemType.name}:
                                </label>
                                <input
                                    type="number"
                                    id={`price-${itemType.id}`}
                                    name={`price-${itemType.id}`}
                                    value={formData.prices[itemType.id]}
                                    onChange={(e) => handlePriceChange(e, itemType.id)}
                                    required
                                />
                            </div>
                        ))}
                    </div>
                    <button type="submit" className="submitButton">บันทึก</button>
                </form>
            )}
        </div>
    );
};

export default SalePriceListCreate;
